const DEFAULT_SESSION_STORAGE_KEY = "asha_slideshow_session_id";
const SESSION_MAP_STORAGE_KEY = "asha_gated_content_sessions";

const safeWindow = () =>
  typeof window !== "undefined" ? window : undefined;

const safeDocument = () =>
  typeof document !== "undefined" ? document : undefined;

const getCurrentPath = () => safeWindow()?.location?.pathname || "/";

const normalizePath = (path) => {
  if (!path || typeof path !== "string") return "/";
  return path.startsWith("/") ? path : `/${path}`;
};

const slugifyPath = (path) => {
  const normalized = normalizePath(path)
    .replace(/^\/+|\/+$/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

  return normalized || "home";
};

const inferContentType = (path) => {
  const normalized = normalizePath(path);
  if (normalized.startsWith("/blog/")) return "blog";
  if (normalized.startsWith("/quote/")) return "quote";
  if (normalized.startsWith("/offer/")) return "offer";
  const slideshowRoutes = new Set([
    "/slideshow",
    "/pms",
    "/os",
    "/os-alt",
    "/ai-os",
    "/os-1",
    "/os-2",
    "/os-v2",
    "/os-v3",
    "/os-v3-backup",
    "/os-fixed",
    "/os-teaser",
  ]);
  if (
    normalized.includes("slideshow") ||
    slideshowRoutes.has(normalized)
  ) {
    return "slideshow";
  }
  return "document";
};

const readSessionMap = () => {
  try {
    const raw = safeWindow()?.localStorage?.getItem(SESSION_MAP_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeSessionMap = (sessionsByRoute) => {
  try {
    safeWindow()?.localStorage?.setItem(
      SESSION_MAP_STORAGE_KEY,
      JSON.stringify(sessionsByRoute)
    );
  } catch {
    // localStorage can be unavailable in private browsing or full-quota cases.
  }
};

export const getApiBaseUrl = () => {
  const hostname = safeWindow()?.location?.hostname;
  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";

  return (
    process.env.REACT_APP_API_URL ||
    (isLocalhost ? "http://localhost:8001" : "")
  );
};

export const buildContentTrackingMetadata = (overrides = {}) => {
  const win = safeWindow();
  const doc = safeDocument();
  const route = normalizePath(overrides.contentRoute || win?.location?.pathname);

  return {
    contentId: overrides.contentId || slugifyPath(route),
    contentType: overrides.contentType || inferContentType(route),
    contentTitle: overrides.contentTitle || doc?.title || route,
    contentRoute: route,
    contentUrl: overrides.contentUrl || win?.location?.href,
    referrer:
      overrides.referrer !== undefined ? overrides.referrer : doc?.referrer,
  };
};

export const getScopedSessionStorageKey = (
  sessionStorageKey = DEFAULT_SESSION_STORAGE_KEY,
  route = getCurrentPath()
) => `${sessionStorageKey}:${slugifyPath(route)}`;

export const storeContentSession = (
  sessionId,
  sessionStorageKey = DEFAULT_SESSION_STORAGE_KEY,
  metadata = buildContentTrackingMetadata()
) => {
  if (!sessionId) return;

  try {
    const storage = safeWindow()?.localStorage;
    if (!storage) return;

    const route = normalizePath(metadata.contentRoute || getCurrentPath());
    storage.setItem(sessionStorageKey, sessionId);
    storage.setItem(getScopedSessionStorageKey(sessionStorageKey, route), sessionId);

    const sessionsByRoute = readSessionMap();
    sessionsByRoute[route] = {
      sessionId,
      sessionStorageKey,
      contentId: metadata.contentId,
      contentType: metadata.contentType,
      contentTitle: metadata.contentTitle,
      updatedAt: Date.now(),
    };
    writeSessionMap(sessionsByRoute);

    safeWindow()?.dispatchEvent(
      new CustomEvent("asha:gated-content-session", {
        detail: sessionsByRoute[route],
      })
    );
  } catch {
    // Access should still be granted if client-side persistence fails.
  }
};

export const getStoredContentSessionForRoute = (route = getCurrentPath()) => {
  const normalizedRoute = normalizePath(route);
  const entry = readSessionMap()[normalizedRoute];
  return entry?.sessionId ? entry : null;
};

export const getStoredContentSessionId = (
  sessionStorageKey = DEFAULT_SESSION_STORAGE_KEY,
  options = {}
) => {
  const route = normalizePath(options.route || getCurrentPath());
  const storage = safeWindow()?.localStorage;
  if (!storage) return null;

  const scopedSession = storage.getItem(
    getScopedSessionStorageKey(sessionStorageKey, route)
  );
  if (scopedSession) return scopedSession;

  const routeSession = readSessionMap()[route]?.sessionId;
  if (routeSession) return routeSession;

  return options.includeLegacy ? storage.getItem(sessionStorageKey) : null;
};

export const hasStoredContentAccess = (
  accessStorageKey,
  sessionStorageKey = DEFAULT_SESSION_STORAGE_KEY
) => {
  const storage = safeWindow()?.localStorage;
  return Boolean(
    storage?.getItem(accessStorageKey) &&
      getStoredContentSessionId(sessionStorageKey)
  );
};

export const getDefaultSessionStorageKey = () => DEFAULT_SESSION_STORAGE_KEY;
